import { DestroyRef, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { BrandAsset, PreModeration, PreModerationComponent, SbCreativeType } from "@front/m19-api-client";
import { AccountSelectionService, SbStrategyService } from "@front/m19-services";
import { ToasterService } from "@front/m19-ui";
import { take } from "rxjs";

export type SbPreModerationWarnings = {
  headline: string;
  brandLogo: string;
  brandLogoUrl: string;
};

export function shouldSkipSbPreModeration(
  creativeType: SbCreativeType,
  headline: string | null | undefined,
  brandLogo: BrandAsset | null | undefined,
  skip = false,
): boolean {
  if (skip) return true;
  if (creativeType !== SbCreativeType.manualCollection) return false;
  return !headline?.trim() && !brandLogo;
}

export function sbPreModerationWarningsFromResponse(res: PreModeration): SbPreModerationWarnings | null {
  const headlineComponent = findPreModerationComponent(res.textComponents, "headline");
  const brandLogoComponent = findPreModerationComponent(res.imageComponents, "brandLogo");

  const warnings: SbPreModerationWarnings = {
    headline: headlineComponent?.policyViolations?.[0]?.policyDescription ?? "",
    brandLogo: brandLogoComponent?.policyViolations?.[0]?.policyDescription ?? "",
    brandLogoUrl: brandLogoComponent?.policyViolations?.[0]?.policyLinkUrl ?? "",
  };
  return warnings.headline || warnings.brandLogo ? warnings : null;
}

function findPreModerationComponent(
  components: PreModerationComponent[] | undefined,
  componentId: keyof SbPreModerationWarnings,
) {
  return components?.find((component) => component.id == componentId);
}

@Injectable()
export class SbPreModerationService {
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  readonly hasLaunched = signal(false);
  readonly warning = signal<SbPreModerationWarnings | null>(null);
  readonly loading = signal(false);

  reset() {
    this.hasLaunched.set(false);
    this.warning.set(null);
    this.loading.set(false);
  }

  shouldSkip(
    creativeType: SbCreativeType,
    headline: string | null | undefined,
    brandLogo: BrandAsset | null | undefined,
  ): boolean {
    return shouldSkipSbPreModeration(creativeType, headline, brandLogo);
  }

  launch(headline: string | undefined, brandLogoUrl: string | undefined, onNoWarning?: () => void) {
    if (this.loading()) return;

    const am = this.accountMarketplace();
    this.loading.set(true);

    this.sbStrategyService
      .sbPreModeration(am.accountId, am.marketplace, headline, brandLogoUrl, [])
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.hasLaunched.set(true);
          const warnings = sbPreModerationWarningsFromResponse(res);
          this.warning.set(warnings);
          this.loading.set(false);
          if (!warnings) onNoWarning?.();
        },
        error: (err) => {
          this.loading.set(false);
          this.toasterService.error(err);
        },
      });
  }
}
